<script setup lang="ts">
/**
 * El recibo, como se ve.
 *
 * Portado del `invoice preview` de la plantilla: el comprobante en una tarjeta a
 * la izquierda y las acciones en una columna a la derecha. Es la pantalla de
 * **consulta** — el editor es para armarlo, esto es para mirarlo, imprimirlo y,
 * más adelante, enviarlo.
 *
 * 🪤 **No imprime por su cuenta.** Podría hacerlo con un `@media print` y sería
 * más rápido, pero entonces habría dos formas de producir el mismo papel —esta y
 * el PDF— y ninguna sería claramente la buena. Es el mismo problema que ya se
 * descartó con los tres caminos de recibo de §31.2. El PDF sigue siendo el único
 * que emite el documento; esta pantalla lo muestra y lo abre.
 */
import { apiOrigin } from '@/api/client'
import {
  type Receipt,
  type ReceiptLine,
  createReceiptPdfLink,
  getReceipt,
  sendReceipt,
} from '@/api/receipts'
import { getClient } from '@/api/clients'
import { getDiveCenter } from '@/api/dive-centers'
import { receiptRows } from '@/data/receiptRows'
import AlertBox from '@/components/AlertBox.vue'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { usePreferences } from '@/composables/usePreferences'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'
import type { DiveCenter } from '@/types'

definePage({
  meta: {
    requiresAuth: true,

    // Sin item propio en el menú: se marca Receipts.
    navActiveLink: 'sales-receipts',
  },
})

const route = useRoute('sales-receipts-receipt-id-preview')
const receiptId = String(route.params.receiptId)

const { currentId } = useCurrentDiveCenter()
const { formatCurrency, formatDate } = usePreferences()

const receipt = ref<Receipt | null>(null)
const diveCenter = ref<DiveCenter | null>(null)
const loading = ref(true)
const error = ref('')
const pdfLoading = ref(false)

const STATUS_CHIP = {
  draft: { label: 'Draft', color: 'secondary' },
  paid: { label: 'Paid', color: 'success' },
  void: { label: 'Void', color: 'error' },
} as const

/** Las líneas de dirección del centro, como en la cabecera del comprobante. */
const issuerAddress = computed(() => {
  const a = diveCenter.value?.address

  if (!a)
    return []

  return [
    a.formattedAddress ?? [a.street, a.city].filter(Boolean).join(', '),
    [a.postalCode, a.country].filter(Boolean).join(' '),
  ].filter(Boolean)
})

const issuerContact = computed(() =>
  [diveCenter.value?.contactPhone, diveCenter.value?.contactEmail].filter(Boolean),
)

/*
 * El envío del comprobante (§48).
 *
 * El correo sale de Blue Trails —el dominio del centro no está autorizado a
 * despachar por este servidor—, así que no hay campo "From" que elegir: sería
 * ofrecer una decisión que no existe. El centro figura en el cuerpo y en el
 * `replyTo`.
 */
const sendOpen = ref(false)
const sending = ref(false)
const sendTo = ref('')
const sendSubject = ref('')
const sendMessage = ref('')
const sendFormRef = ref()

/** El mail de la ficha, si lo tiene. Si no, lo escribe quien envía y se guarda. */
const clientEmail = ref('')

const emailRule = (v: string) =>
  (!!v && /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/.test(v)) || 'Enter a valid email address.'

const requiredRule = (v: string) => (!!v && !!v.trim()) || 'Required.'

/*
 * Asunto y mensaje genéricos, con el nombre del centro. Se precargan cada vez
 * que se abre: si se cambió de recibo, un borrador viejo diría otro número.
 */
function openSend() {
  const center = diveCenter.value?.name ?? 'the dive centre'
  const number = receipt.value?.number ? ` ${receipt.value.number}` : ''

  sendTo.value = clientEmail.value
  sendSubject.value = `Your receipt${number} from ${center}`
  sendMessage.value
    = `Hi ${receipt.value?.clientName ?? ''},\n\n`
    + `Thank you for diving with ${center}. Your receipt is attached to this email.\n\n`
    + 'We hope to see you again soon!'

  sendOpen.value = true
}

async function submitSend() {
  const dcId = currentId.value

  if (!dcId || !receipt.value)
    return

  const { valid } = await sendFormRef.value.validate()

  if (!valid)
    return

  sending.value = true
  try {
    const { receipt: saved } = await sendReceipt(dcId, receipt.value.id, {
      to: sendTo.value.trim(),
      subject: sendSubject.value.trim(),
      message: sendMessage.value.trim(),
    })

    receipt.value = saved

    // Queda para el próximo envío sin volver a pedir la ficha.
    clientEmail.value = saved.sentTo ?? sendTo.value.trim()
    sendOpen.value = false
    notifySuccess(`Receipt sent to ${saved.sentTo ?? sendTo.value.trim()}.`)
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not send the receipt.'))
  }
  finally {
    sending.value = false
  }
}

async function load() {
  const dcId = currentId.value

  if (!dcId)
    return

  loading.value = true
  error.value = ''
  try {
    const [res, dcRes] = await Promise.all([getReceipt(dcId, receiptId), getDiveCenter(dcId)])

    receipt.value = res.receipt
    diveCenter.value = dcRes.diveCenter

    /*
     * La ficha, solo para el mail: no se pide junto con las otras dos porque el
     * recibo puede haber perdido el puntero al cliente (se borró la ficha y el
     * nombre quedó congelado), y ahí no hay a quién escribirle de entrada.
     * Best-effort — sin esto el panel se abre con el campo vacío, que es
     * exactamente lo que hay que hacer.
     */
    if (res.receipt.clientId) {
      const client = await getClient(res.receipt.clientId).catch(() => null)

      clientEmail.value = client?.client.email ?? ''
    }
  }
  catch (e) {
    error.value = apiMessage(e, 'Could not load this receipt.')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

/*
 * 🪤 **Y al cambiar el centro, que en un refresh llega DESPUÉS.**
 *
 * `currentId` se resuelve de forma asíncrona al arrancar la sesión. Entrando por
 * el listado ya está puesto, pero recargando esta URL directamente `onMounted`
 * corre con el centro todavía sin resolver: `load` se iba por el `return` de
 * arriba y la pantalla quedaba con la barra de carga puesta para siempre. Con el
 * watch, la primera resolución dispara la carga real.
 */
watch(currentId, load)

/**
 * 🪤 La pestaña se abre **antes** del `await`: un `window.open` posterior a una
 * espera ya no cuenta como gesto del usuario y lo mata el bloqueador (§21.7).
 */
async function openPdf() {
  const dcId = currentId.value

  if (!dcId)
    return

  const tab = window.open('', '_blank')

  pdfLoading.value = true
  try {
    const { url } = await createReceiptPdfLink(dcId, receiptId)

    const absolute = new URL(url, apiOrigin()).toString()

    if (tab)
      tab.location.href = absolute
    else
      window.open(absolute, '_blank')
  }
  catch (e) {
    tab?.close()
    notifyError(apiMessage(e, 'Could not open the receipt.'))
  }
  finally {
    pdfLoading.value = false
  }
}

const currency = computed(() => receipt.value?.currency ?? 'EUR')

/*
 * Como en el resto del portal, la moneda se escribe como diga Preferences. El
 * recibo no es una excepción: el listado, el editor, el preview y el PDF leen
 * todos la misma preferencia, así que el mismo cobro se escribe igual en los
 * cuatro sin necesidad de forzar nada.
 */
const money = (amount: number) => formatCurrency(amount, currency.value)

/**
 * La salida de la que vino una línea. Vacío en las cargadas a mano.
 *
 * El título solo se repite cuando aporta: en la línea de la actividad es el
 * mismo texto que el rótulo, y en un alquiler dice a qué salida acompañó.
 */
/**
 * El chip de tipo de cada renglón: mismo lenguaje que el panel del catálogo.
 *
 * Con diez o veinte líneas seguidas, "BCD" y "Night dives" se leen igual y hay
 * que reconstruir de memoria cuál es la actividad y cuál el equipo. El chip lo
 * dice de un vistazo y con el mismo color e ícono que tienen Services y Rentals
 * en el menú, así no hay un segundo código visual que aprender.
 *
 * `other` es una línea escrita a mano — no salió del catálogo y no tiene tipo
 * que declarar más allá de eso.
 */
function lineKind(line: ReceiptLine): { label: string; color: string; icon: string } {
  if (line.kind === 'rental')
    return { label: 'Rental', color: 'info', icon: 'ri-price-tag-3-line' }

  if (line.kind === 'activity')
    return { label: 'Service', color: 'primary', icon: 'ri-calendar-2-line' }

  return { label: 'Manual', color: 'secondary', icon: 'ri-pencil-line' }
}

/**
 * De qué salida vino el renglón (§57).
 *
 * La fecha y la hora se quedan acá: sacarlas a una cabecera por día costaba una
 * fila entera por día. El título se va, porque ya lo dice la fila de la que
 * cuelga — salvo en un alquiler que no tiene su actividad en el recibo.
 */
function lineDetail(line: ReceiptLine, child: boolean): string {
  if (!line.activityDate)
    return ''

  const parts = [formatDate(line.activityDate)]

  if (line.activityTime)
    parts.push(line.activityTime)

  const hasParent = !!line.bookingId
    && (receipt.value?.lines ?? []).some(l => l.kind === 'activity' && l.bookingId === line.bookingId)

  if (child && !hasParent && line.activityTitle && line.activityTitle !== line.label)
    parts.push(line.activityTitle)

  return parts.join(' · ')
}

/** Las filas como se dibujan: día, actividad, y sus alquileres debajo (§57). */
const rows = computed(() => receiptRows(receipt.value?.lines ?? []))
</script>

<template>
  <div>
    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
    />

    <AlertBox
      variant="danger"
      :message="error"
    />

    <VRow v-if="receipt">
      <VCol
        cols="12"
        md="9"
      >
        <VCard>
          <VCardText class="pa-sm-8">
            <!--
              Cabecera: emisor a la izquierda, número y fechas a la derecha,
              sobre el bloque gris de la plantilla.
            -->
            <div class="bt-doc-head d-flex flex-wrap justify-space-between ga-6 pa-6 rounded mb-8">
              <div>
                <!--
                  El logo del centro junto al nombre, como en el PDF: el
                  comprobante en pantalla y el papel llevan el mismo membrete.
                -->
                <div class="d-flex align-center ga-3 mb-3">
                  <VAvatar
                    v-if="diveCenter?.logoUrl"
                    size="44"
                    rounded
                    :image="diveCenter.logoUrl"
                  />
                  <div class="text-h5 font-weight-bold">
                    {{ diveCenter?.name ?? '—' }}
                  </div>
                </div>
                <div class="text-body-2 text-medium-emphasis">
                  <div
                    v-for="(l, i) in issuerAddress"
                    :key="i"
                  >
                    {{ l }}
                  </div>
                  <div v-if="issuerContact.length">
                    {{ issuerContact.join(' · ') }}
                  </div>
                </div>
              </div>

              <div class="text-md-end">
                <div class="text-h6 mb-3">
                  {{ receipt.number ? `Receipt #${receipt.number}` : 'Receipt' }}
                </div>
                <div class="text-body-2 text-medium-emphasis">
                  <div>Date issued: {{ formatDate(receipt.createdAt) }}</div>
                  <div v-if="receipt.paidAt">
                    Paid: {{ formatDate(receipt.paidAt) }}
                  </div>
                </div>
              </div>
            </div>

            <!--
              Rótulo y dato en la MISMA línea. Un nombre y un chip debajo de su
              título gastaban dos renglones para dos palabras, y empujaban la
              tabla —que es lo que se viene a leer— fuera del primer golpe de
              vista. Los dos bloques a la vez: dejar uno en dos líneas mantiene
              la altura y no se gana nada.
            -->
            <div class="d-flex flex-wrap justify-space-between ga-6 mb-6">
              <div class="d-flex align-center ga-2">
                <h6 class="text-h6">
                  Receipt to:
                </h6>
                <span class="text-body-2 text-medium-emphasis">
                  {{ receipt.clientName }}
                </span>
              </div>

              <div class="d-flex align-center ga-2">
                <h6 class="text-h6">
                  Status
                </h6>
                <VChip
                  :color="STATUS_CHIP[receipt.status].color"
                  size="small"
                >
                  {{ STATUS_CHIP[receipt.status].label }}
                </VChip>
                <!-- El motivo sí queda aparte: es una frase, no una etiqueta. -->
                <span
                  v-if="receipt.voidReason"
                  class="text-body-2 text-medium-emphasis"
                >
                  {{ receipt.voidReason }}
                </span>
              </div>
            </div>

            <VTable class="border rounded text-no-wrap mb-6">
              <thead>
                <tr>
                  <th>Item</th>
                  <th class="text-end">
                    Unit price
                  </th>
                  <th class="text-end">
                    Qty
                  </th>
                  <th class="text-end">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- Una línea de separación al cambiar de día, y nada más (§57). -->
                <tr
                  v-for="row in rows"
                  :key="row.index"
                  :class="row.firstOfDay ? 'bt-day-start' : ''"
                >
                  <!--
                    El mismo avatar + dos líneas del editor y de la tabla de
                    la plantilla: el comprobante en pantalla se lee igual que
                    donde se armó.
                  -->
                  <td :class="row.child ? 'ps-8' : ''">
                    <div class="d-flex align-center">
                      <VAvatar
                        size="32"
                        :color="lineKind(row.line).color"
                        variant="tonal"
                        rounded
                      >
                        <VIcon
                          :icon="lineKind(row.line).icon"
                          size="18"
                        />
                      </VAvatar>
                      <div class="d-flex flex-column ms-3">
                        <span class="d-block font-weight-medium text-high-emphasis text-truncate">
                          {{ row.line.label }}
                        </span>
                        <small v-if="lineDetail(row.line, row.child)">{{ lineDetail(row.line, row.child) }}</small>
                      </div>
                    </div>
                  </td>
                  <td class="text-end">
                    {{ money(row.line.unitAmount) }}
                  </td>
                  <td class="text-end">
                    {{ row.line.quantity }}
                  </td>
                  <td class="text-end font-weight-medium">
                    {{ money(row.line.amount ?? row.line.unitAmount * row.line.quantity) }}
                  </td>
                </tr>

                <tr v-if="!receipt.lines.length">
                  <td
                    colspan="4"
                    class="text-center text-medium-emphasis py-6"
                  >
                    No lines.
                  </td>
                </tr>
              </tbody>
            </VTable>

            <div class="d-flex flex-wrap justify-space-between ga-6">
              <div
                class="text-body-2 text-medium-emphasis"
                style="max-inline-size: 60ch; white-space: pre-line"
              >
                {{ receipt.notes }}
              </div>

              <div
                class="text-end"
                style="min-inline-size: 220px"
              >
                <!--
                  Subtotal y descuento solo si hay descuento: sin él, el
                  subtotal sería el total escrito dos veces.
                -->
                <template v-if="receipt.discount">
                  <div class="d-flex justify-space-between text-body-2 text-medium-emphasis">
                    <span>Subtotal</span>
                    <span>{{ money(receipt.subtotal) }}</span>
                  </div>
                  <div class="d-flex justify-space-between text-body-2 text-medium-emphasis">
                    <span>Discount</span>
                    <span>-{{ money(receipt.discount) }}</span>
                  </div>
                  <VDivider class="my-2" />
                </template>

                <div class="d-flex justify-space-between align-baseline">
                  <span class="text-caption text-medium-emphasis">Total</span>
                  <span class="text-h4">{{ money(receipt.total) }}</span>
                </div>
              </div>
            </div>

            <VDivider class="my-6" />

            <!--
              La misma advertencia que el PDF, con el mismo peso: es la única
              frase que decide qué se puede hacer con el comprobante.
            -->
            <VAlert
              variant="outlined"
              density="compact"
              class="text-body-2"
            >
              <strong>This is not a tax invoice.</strong> It is a record of the activities and
              amounts charged by the dive centre, issued for reference only. It has no fiscal
              validity and cannot be used for tax or accounting purposes.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="3"
      >
        <VCard>
          <VCardText class="d-flex flex-column ga-3">
            <VBtn
              block
              prepend-icon="ri-file-pdf-2-line"
              :loading="pdfLoading"
              @click="openPdf"
            >
              Receipt PDF
            </VBtn>

            <!--
              Solo un cobrado se manda: un borrador no tiene número y el papel
              lleva estampado "DRAFT — not paid yet", así que enviarlo invita a
              pagar contra algo que todavía puede cambiar. Presupuestar va a ser
              otro documento (§47).
            -->
            <VBtn
              v-if="receipt.status === 'paid'"
              block
              variant="outlined"
              prepend-icon="ri-mail-send-line"
              @click="openSend"
            >
              Send by email
            </VBtn>

            <!-- Para no tener que preguntarle al cliente si le llegó. -->
            <div
              v-if="receipt.sentAt"
              class="text-caption text-medium-emphasis"
            >
              Sent to {{ receipt.sentTo }} on {{ formatDate(receipt.sentAt) }}
            </div>

            <!-- Editar solo tiene sentido en un borrador: lo cobrado se anula. -->
            <VBtn
              v-if="receipt.status === 'draft'"
              block
              variant="outlined"
              prepend-icon="ri-pencil-line"
              :to="`/sales/receipts/${receipt.id}`"
            >
              Edit
            </VBtn>

            <VBtn
              block
              variant="text"
              prepend-icon="ri-arrow-left-line"
              to="/sales/receipts"
            >
              All receipts
            </VBtn>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!--
      🪤 En la RAÍZ de la página, no dentro de la tarjeta: un `VNavigationDrawer`
      dentro de un contexto de apilado propio queda recortado y con el velo
      puesto solo sobre esa tarjeta (§24.8).
    -->
    <VNavigationDrawer
      v-model="sendOpen"
      temporary
      location="end"
      :width="$vuetify.display.smAndDown ? undefined : 460"
      class="bt-send"
    >
      <div class="d-flex align-center justify-space-between pa-4">
        <div class="text-h6">
          Send receipt
        </div>
        <VBtn
          icon="ri-close-line"
          variant="text"
          size="small"
          @click="sendOpen = false"
        />
      </div>

      <VDivider />

      <VForm
        ref="sendFormRef"
        class="pa-4 d-flex flex-column ga-4"
        @submit.prevent="submitSend"
      >
        <!--
          Sin campo "From": el correo sale de Blue Trails porque el dominio del
          centro no está autorizado a despachar por este servidor. Ofrecerlo
          sería ofrecer una decisión que no existe.
        -->
        <VTextField
          v-model="sendTo"
          label="To"
          type="email"
          :rules="[emailRule]"
          hint="Saved to the client's record if they didn't have one."
          persistent-hint
        />

        <VTextField
          v-model="sendSubject"
          label="Subject"
          :rules="[requiredRule]"
        />

        <VTextarea
          v-model="sendMessage"
          label="Message"
          rows="8"
          :rules="[requiredRule]"
        />

        <div>
          <VChip
            color="success"
            variant="tonal"
            prepend-icon="ri-attachment-2"
          >
            Receipt attached
          </VChip>
        </div>

        <div class="d-flex ga-3">
          <VBtn
            type="submit"
            :loading="sending"
          >
            Send
          </VBtn>
          <VBtn
            variant="outlined"
            color="secondary"
            :disabled="sending"
            @click="sendOpen = false"
          >
            Cancel
          </VBtn>
        </div>
      </VForm>
    </VNavigationDrawer>
  </div>
</template>

<style scoped>
/*
 * 🪤 `.v-navigation-drawer__content` viene con `overflow-y: hidden` (§20.7): sin
 * esto el mensaje largo no se puede recorrer y los botones quedan fuera de
 * alcance en una pantalla baja.
 */
.bt-send :deep(.v-navigation-drawer__content) {
  overflow-y: auto;
}
</style>

<style scoped>
/* El bloque gris de la cabecera del comprobante, como en la plantilla. */
.bt-doc-head {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

/*
 * El cambio de día: una línea algo más marcada que la de entre renglones, la
 * misma del editor (§57).
 */
.bt-day-start td {
  border-block-start: 2px solid rgba(var(--v-border-color), 0.28);
}
</style>
